import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navegarComCtrl } from "../../utils/solicitacoes/navegacao";
import Chart from "react-apexcharts";
import { CustomTable } from "../../componentes/table/CustomTable";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import StatusBadge from "../../components/solicitacoes/Shared/StatusBadge";
import { ChartCard } from "../../componentes/chart/CardChart";
import TabelaEntriesGrafico from "../../components/solicitacoes/Dashboard/TabelaEntriesGrafico";
import DashboardProcessoLayout, { Campo, INPUT_CLASS } from "../../components/solicitacoes/Dashboard/DashboardProcessoLayout";
import { useDashboardProcesso } from "../../hooks/solicitacoes/useDashboardProcesso";
import LancamentoDespesaService from "../../services/resource/lancamentoDespesaService";
import UsuarioService from "../../services/resource/usuarioService";
import FilialService from "../../services/resource/filialService";
import { STATUS_CONFIG } from "../../utils/solicitacoes/statusConfig";
import { processoInfo } from "../../utils/solicitacoes/processoRegistry";
import { formatarMoeda } from "../../utils/solicitacoes/formatters";
import { donutOptions, barOptions, CORES_GRAFICO } from "../../utils/solicitacoes/chartOptions";

const service = new LancamentoDespesaService();
const usuarioService = new UsuarioService();
const filialService = new FilialService();

const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, info]) => ({ value, label: info.label }));
const TIPO_DESPESA_OPTIONS = [
    { value: "SUPRIMENTOS", label: "Suprimentos" }, { value: "CONTAS_A_PAGAR", label: "Contas a Pagar" },
];
const ETAPA_OPTIONS = (processoInfo("LANCAMENTO_DESPESA")?.etapas || []).map(e => ({ value: e.codigo, label: e.label }));

const FILTROS_INICIAIS_EXTRA = {
    filialId: null, fornecedorNome: "", tipoDespesa: null, tipoPagamento: "", contaBpNome: "",
    status: null, etapaAtual: null, solicitanteId: null, responsavelId: null,
    vencimentoInicial: "", vencimentoFinal: "",
};

const DATA_MINIMA = "2020-01-01";
const DATA_MAXIMA = "2099-12-31";

// Os períodos de solicitação e de vencimento nunca vão juntos: se o vencimento foi informado,
// a solicitação vira 01/01/2020 até o final; o vencimento incompleto é completado do mesmo jeito.
function prepararFiltros(filtros) {
    if (!filtros.vencimentoInicial && !filtros.vencimentoFinal) return filtros;
    return {
        ...filtros,
        dataInicial: DATA_MINIMA,
        dataFinal: DATA_MAXIMA,
        vencimentoInicial: filtros.vencimentoInicial || DATA_MINIMA,
        vencimentoFinal: filtros.vencimentoFinal || DATA_MAXIMA,
    };
}

function resumoLista(itens, vazio = "-") {
    if (!itens || itens.length === 0) return vazio;
    const primeiras = itens.slice(0, 2).join(", ");
    const resto = itens.length > 2 ? ` +${itens.length - 2}` : "";
    return (
        <span title={itens.join("\n")} className="block max-w-[220px] truncate">
            {primeiras}{resto}
        </span>
    );
}

const COLUNAS = [
    { key: "numero", label: "Número", width: 120 },
    { key: "dataSolicitacao", label: "Data", format: "date" },
    { key: "solicitanteNome", label: "Solicitante" },
    { key: "filialNome", label: "Filial" },
    { key: "fornecedorNome", label: "Fornecedor" },
    { key: "descricao", label: "Descrição" },
    { key: "tipoDespesa", label: "Tipo de Despesa" },
    { key: "tipoPagamento", label: "Tipo de Pagamento" },
    { key: "contasBp", label: "Conta BP", sortable: false, filterable: false, render: v => resumoLista(v) },
    { key: "numerosNotaFiscal", label: "Notas Fiscais", sortable: false, filterable: false, render: v => resumoLista(v) },
    { key: "valorTotal", label: "Valor Total", format: "currency" },
    { key: "vencimento", label: "Vencimento", format: "date" },
    { key: "status", label: "Status", render: v => <StatusBadge status={v} />, center: true },
    { key: "etapaAtual", label: "Etapa Atual" },
    { key: "responsavelAtualNome", label: "Responsável" },
];

export default function DashboardLancamentoDespesa() {
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);
    const [filiais, setFiliais] = useState([]);

    const { filtros, setFiltros, limparFiltros, pesquisar, linhas, dashboard, loading } = useDashboardProcesso({
        service, chaveFiltros: "solicitacoes.dashboard.despesa.filtros", filtrosIniciaisExtra: FILTROS_INICIAIS_EXTRA,
        prepararFiltros,
    });

    useEffect(() => {
        document.title = "MyDash | Dashboard de Lançamento de Despesa";
    }, []);

    useEffect(() => {
        usuarioService.listarUsuarios().then(res => setUsuarios(res.data)).catch(() => setUsuarios([]));
        filialService.listarAtivas().then(res => setFiliais(res.data)).catch(() => setFiliais([]));
    }, []);

    function setFiltro(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    const porStatusEntries = Object.entries(dashboard?.porStatus || {}).map(([k, v]) => [STATUS_CONFIG[k]?.label || k, v]);
    const porEtapaEntries = Object.entries(dashboard?.porEtapa || {});
    const evolucaoEntries = Object.entries(dashboard?.evolucaoPeriodo || {});
    const valorPorFilialFinalizadoEntries = Object.entries(dashboard?.valorPorFilialFinalizado || {});
    const valorPorTipoDespesaFinalizadoEntries = Object.entries(dashboard?.valorPorTipoDespesaFinalizado || {});
    const evolucaoValorFinalizadoEntries = Object.entries(dashboard?.evolucaoValorFinalizado || {});

    const opcoesBarraValor = {
        yaxis: { labels: { formatter: v => formatarMoeda(v) } },
        tooltip: { y: { formatter: v => formatarMoeda(v) } },
    };

    const [viewStatus, setViewStatus] = useState("chart");
    const [viewEtapa, setViewEtapa] = useState("chart");
    const [viewEvolucao, setViewEvolucao] = useState("chart");
    const [viewValorFilial, setViewValorFilial] = useState("chart");
    const [viewValorTipo, setViewValorTipo] = useState("chart");
    const [viewEvolucaoValor, setViewEvolucaoValor] = useState("chart");

    return (
        <DashboardProcessoLayout
            compacto
            titulo="Dashboard de Lançamento de Despesa"
            onNovaSolicitacao={() => navigate("/solicitacoes/nova")}
            loading={loading}
            filtros={filtros}
            onFiltroChange={setFiltro}
            onPesquisar={pesquisar}
            onLimparFiltros={limparFiltros}
            kpis={[
                { label: "Total de solicitações", value: dashboard?.total ?? 0, icon: "fa fa-file-invoice-dollar", iconClassName: "bg-primary/10 text-primary" },
                { label: "Pendentes", value: dashboard?.pendentes ?? 0, icon: "fa fa-clock", iconClassName: "bg-amber-100 text-amber-700" },
                { label: "Finalizadas", value: dashboard?.finalizadas ?? 0, icon: "fa fa-check-circle", iconClassName: "bg-success/10 text-success" },
                { label: "Valor Total", value: formatarMoeda(dashboard?.valorTotal ?? 0), icon: "fa fa-sack-dollar", iconClassName: "bg-blue-100 text-blue-700" },
                { label: "Valor Total (Finalizadas)", value: formatarMoeda(dashboard?.valorTotalFinalizado ?? 0), icon: "fa fa-coins", iconClassName: "bg-success/10 text-success", colspan: 'lg:col-span-2' },
                { label: "Valor Médio (Finalizadas)", value: formatarMoeda(dashboard?.valorMedioFinalizado ?? 0), icon: "fa fa-calculator", iconClassName: "bg-indigo-100 text-indigo-700", colspan: 'lg:col-span-2' },
            ]}
            graficos={(
                <>
                    <div className="lg:col-span-6">
                        <ChartCard title="Despesas por status" view={viewStatus} onViewChange={setViewStatus} accent={CORES_GRAFICO[0]}
                            tableContent={<TabelaEntriesGrafico entries={porStatusEntries} colunaValor="Quantidade" />}>
                            {porStatusEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="donut" height="100%" series={porStatusEntries.map(([, v]) => v)}
                                    options={donutOptions(porStatusEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>
                    <div className="lg:col-span-6">
                        <ChartCard title="Despesas por etapa" view={viewEtapa} onViewChange={setViewEtapa} accent={CORES_GRAFICO[1]}
                            tableContent={<TabelaEntriesGrafico entries={porEtapaEntries} colunaValor="Quantidade" />}>
                            {porEtapaEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="donut" height="100%" series={porEtapaEntries.map(([, v]) => v)}
                                    options={donutOptions(porEtapaEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>
                    <div className="lg:col-span-6">
                        <ChartCard title="Evolução no período" view={viewEvolucao} onViewChange={setViewEvolucao} accent={CORES_GRAFICO[2]}
                            tableContent={<TabelaEntriesGrafico entries={evolucaoEntries} colunaValor="Quantidade" />}>
                            {evolucaoEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="bar" height="100%" series={[{ name: "Solicitações", data: evolucaoEntries.map(([, v]) => v) }]}
                                    options={barOptions(evolucaoEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>

                    <div className="lg:col-span-6">
                        <ChartCard title={<>Valor finalizado por filial<br /><span className="text-xs font-normal text-muted-foreground">Top 10 por valor — só solicitações finalizadas</span></>}
                            view={viewValorFilial} onViewChange={setViewValorFilial} accent={CORES_GRAFICO[3]}
                            tableContent={<TabelaEntriesGrafico entries={valorPorFilialFinalizadoEntries} colunaValor="Valor" formatarValor={formatarMoeda} />}>
                            {valorPorFilialFinalizadoEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="bar" height="100%" series={[{ name: "Valor", data: valorPorFilialFinalizadoEntries.map(([, v]) => v) }]}
                                    options={barOptions(valorPorFilialFinalizadoEntries.map(([k]) => k), opcoesBarraValor)} />
                            )}
                        </ChartCard>
                    </div>
                    <div className="lg:col-span-6">
                        <ChartCard title={<>Valor finalizado por tipo de despesa<br /><span className="text-xs font-normal text-muted-foreground">Só solicitações finalizadas</span></>}
                            view={viewValorTipo} onViewChange={setViewValorTipo} accent={CORES_GRAFICO[4]}
                            tableContent={<TabelaEntriesGrafico entries={valorPorTipoDespesaFinalizadoEntries} colunaValor="Valor" formatarValor={formatarMoeda} />}>
                            {valorPorTipoDespesaFinalizadoEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="bar" height="100%" series={[{ name: "Valor", data: valorPorTipoDespesaFinalizadoEntries.map(([, v]) => v) }]}
                                    options={barOptions(valorPorTipoDespesaFinalizadoEntries.map(([k]) => k), opcoesBarraValor)} />
                            )}
                        </ChartCard>
                    </div>

                    <div className="lg:col-span-6">
                        <ChartCard title={<>Evolução do valor finalizado no período<br /><span className="text-xs font-normal text-muted-foreground">Soma do valor total das solicitações finalizadas</span></>}
                            view={viewEvolucaoValor} onViewChange={setViewEvolucaoValor} accent={CORES_GRAFICO[5]}
                            tableContent={<TabelaEntriesGrafico entries={evolucaoValorFinalizadoEntries} colunaValor="Valor" formatarValor={formatarMoeda} />}>
                            {evolucaoValorFinalizadoEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="bar" height="100%" series={[{ name: "Valor", data: evolucaoValorFinalizadoEntries.map(([, v]) => v) }]}
                                    options={barOptions(evolucaoValorFinalizadoEntries.map(([k]) => k), opcoesBarraValor)} />
                            )}
                        </ChartCard>
                    </div>
                </>
            )}
            filtrosExtras={(
                <>
                    <Campo label="Vencimento de">
                        <input type="date" value={filtros.vencimentoInicial ?? ""} onChange={e => setFiltro("vencimentoInicial", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Vencimento até">
                        <input type="date" value={filtros.vencimentoFinal ?? ""} onChange={e => setFiltro("vencimentoFinal", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Filial">
                        <CustomDropdown value={filtros.filialId} onChange={e => setFiltro("filialId", e.value)} options={filiais}
                            optionValue="id" optionLabel="nome" placeholder="Todas as filiais" showClear filter />
                    </Campo>
                    <Campo label="Conta BP">
                        <input type="text" value={filtros.contaBpNome} onChange={e => setFiltro("contaBpNome", e.target.value)}
                            placeholder="Buscar por conta" className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Fornecedor">
                        <input type="text" value={filtros.fornecedorNome} onChange={e => setFiltro("fornecedorNome", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Tipo de Despesa">
                        <CustomDropdown value={filtros.tipoDespesa} onChange={e => setFiltro("tipoDespesa", e.value)} options={TIPO_DESPESA_OPTIONS}
                            placeholder="Todos os tipos" showClear />
                    </Campo>
                    <Campo label="Tipo de Pagamento">
                        <input type="text" value={filtros.tipoPagamento} onChange={e => setFiltro("tipoPagamento", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Status">
                        <CustomDropdown value={filtros.status} onChange={e => setFiltro("status", e.value)} options={STATUS_OPTIONS}
                            placeholder="Todos os status" showClear />
                    </Campo>
                    <Campo label="Etapa atual">
                        <CustomDropdown value={filtros.etapaAtual} onChange={e => setFiltro("etapaAtual", e.value)} options={ETAPA_OPTIONS}
                            placeholder="Todas as etapas" showClear />
                    </Campo>
                    <Campo label="Solicitante">
                        <CustomDropdown value={filtros.solicitanteId} onChange={e => setFiltro("solicitanteId", e.value)} options={usuarios}
                            optionValue="id" optionLabel="nome" placeholder="Todos os solicitantes" showClear filter />
                    </Campo>
                    <Campo label="Responsável">
                        <CustomDropdown value={filtros.responsavelId} onChange={e => setFiltro("responsavelId", e.value)} options={usuarios}
                            optionValue="id" optionLabel="nome" placeholder="Todos os responsáveis" showClear filter />
                    </Campo>
                </>
            )}
        >
            <CustomTable
                data={linhas}
                columns={COLUNAS}
                loading={loading}
                pagination
                pageSize={20}
                showHeader={false}
                className="text-[11px] border shadow-card rounded-xl"
                exportable
                exportFilename="dashboard_lancamento_despesa"
                footer={() => null}
                onRowSelect={(row, e) => navegarComCtrl(navigate, `/solicitacoes/${row.solicitacaoId}`, e)}
            />
        </DashboardProcessoLayout>
    );
}
